/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { posHandler, authHandler } from '../redux/handlers';

import PosAvartarCard from 'components/modals/PosAvartarCard';
import PosSettingCard from 'components/modals/PosSettingCard';
import POSStartSellingModal from 'components/modals/POSStartSellingModal';
import POSStartupConfirmationModal from 'components/modals/POSStartupConfirmationModal';
import POSInUseWarningModal from 'components/modals/POSInUseWarningModal';
import PrinterOutOfPaperModal from 'components/modals/PrinterOutOfPaperModal';
import ModalPrinterSettings from '../components/modals/ModalPrinterSettings';
import NoCashFloatWarningModal from 'components/modals/NoCashFloatWarningModal';
import socketService from '../services/socketService';
import LogoutConfirmModal from '../components/modals/LogoutConfirmModal';
import ModalPOSCashTransactionPrint from '../components/modals/printing-forms/ModalPOSCashTransactionPrint';

export default function POSDeviceManagement() {
  const dispatch = useDispatch();
  const { businessSlug } = useParams();
  const [businessState, setBusinessState] = useState({
    businessName: '',
    branchName: ''
  });
  const navigate = useNavigate();
  const [userState, setUserState] = useState({
    profile: null,
    isInImportantTask: false
  });
  const [skipSetting, setSkipSetting] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [deviceState, setDeviceState] = useState({
    list: [],
    selected: null
  });

  const [sessionState, setSessionState] = useState({
    initialCashAmount: '0.00',
    startData: null
  });

  const [modalStates, setModalStates] = useState({
    isStartSellingOpen: false,
    isStartupConfirmationOpen: false,
    isDeviceInUseWarningOpen: false,
    isPrinterErrorOpen: false,
    isNoCashFloatWarningOpen: false,
    isStartPrintPopupOpen: false
  });

  const updateModalState = (modalName, isOpen) => {
    setModalStates(prev => ({
      ...prev,
      [modalName]: isOpen
    }));
  };

  const processPosDevices = useCallback(posDevices => {
    return posDevices.map(pos => {
      const hasActiveSession = pos?.branchPosSelling?.length > 0;

      const posDevice = {
        id: pos.id,
        deviceType: 'POS',
        posInfo: {
          posName: pos.name,
          vatCode: pos.branch.vat_code,
          posNo: pos.pos_id
        },
        isStarted: hasActiveSession,
        userInfo: {
          fullName: '-',
          startTime: '-',
          endTime: '-',
          duration: '-'
        },
        rawData: pos
      };
      const initUserInfo = posDevice.userInfo;
      if (hasActiveSession) {
        const lastIndex = pos.branchPosSelling.length - 1;
        const activeSelling = pos?.branchPosSelling[lastIndex];
        const user = activeSelling.userBusinessGroup;
        if (user) {
          posDevice.userInfo = getUserInfoFromSession(activeSelling, user);
          posDevice.isImportantTask = user.is_in_important_task_pos || false;
        }
        if (posDevice.userInfo !== initUserInfo) {
          setSkipSetting(true);
        }
      }
      return posDevice;
    });
  }, []);

  const processKitchenDevices = useCallback(kitchenDevices => {
    return (kitchenDevices || []).map(kitchen => {
      const hasActiveSession = kitchen?.branchKitchenSession?.length > 0;

      const kitchenDevice = {
        id: kitchen.id,
        deviceType: 'Kitchen',
        posInfo: {
          posName: kitchen.name,
          vatCode: kitchen.branch.vat_code,
          posNo: kitchen.pos_id
        },
        isStarted: hasActiveSession,
        userInfo: {
          fullName: '-',
          startTime: '-',
          endTime: '-',
          duration: '-'
        },
        rawData: kitchen
      };
      const initUserInfo = kitchenDevice.userInfo;
      if (hasActiveSession) {
        const lastIndex = kitchen.branchKitchenSession.length - 1;
        const activeKitchen = kitchen.branchKitchenSession[lastIndex];
        const user = activeKitchen.userBusinessGroup;
        if (user) {
          kitchenDevice.userInfo = getUserInfoFromKitchenSession(activeKitchen, user);
          kitchenDevice.isImportantTask = false;
        }
        if (kitchenDevice !== initUserInfo) {
          setSkipSetting(true);
        }
      }
      return kitchenDevice;
    });
  }, []);

  const getUserInfoFromSession = (activeSelling, user) => {
    const startDate = new Date(activeSelling.start_time);
    const startHours = startDate.getHours().toString().padStart(2, '0');
    const startMinutes = startDate.getMinutes().toString().padStart(2, '0');

    let duration = '-';
    if (activeSelling.is_open) {
      const now = new Date();
      const diffMs = now - startDate;
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      duration = `${diffHrs} ชั่วโมง ${diffMins} นาที`;
    }

    return {
      fullName: `${user.first_name} ${user.last_name}`,
      startTime: `${startHours}.${startMinutes} น.`,
      endTime: activeSelling.end_time ? new Date(activeSelling.end_time).toLocaleTimeString('th-TH') : '-',
      duration: duration
    };
  };

  const getUserInfoFromKitchenSession = (activeSelling, user) => {
    const startDate = new Date(activeSelling.login_time);
    const startHours = startDate.getHours().toString().padStart(2, '0');
    const startMinutes = startDate.getMinutes().toString().padStart(2, '0');

    let duration = '-';
    if (activeSelling.is_active) {
      const now = new Date();
      const diffMs = now - startDate;
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      duration = `${diffHrs} ชั่วโมง ${diffMins} นาที`;
    }

    return {
      fullName: `${user.first_name} ${user.last_name}`,
      startTime: `${startHours}.${startMinutes} น.`,
      endTime: activeSelling.end_time ? new Date(activeSelling.end_time).toLocaleTimeString('th-TH') : '-',
      duration: duration
    };
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const branchId = localStorage.getItem('branchId');

        const [userProfileResponse, branchInfoResponse, posResponse, kitchenResponse] = await Promise.all([
          dispatch(authHandler.getUserBusinessProfile(businessSlug)),
          dispatch(posHandler.getBranchInfo(businessSlug, branchId)),
          dispatch(posHandler.getBranchPos(businessSlug, branchId)),
          dispatch(posHandler.getBranchKitchen(businessSlug, branchId))
        ]);

        if (userProfileResponse) {
          setUserState(prev => ({ ...prev, profile: userProfileResponse }));
        }

        if (branchInfoResponse) {
          const userRole =
            branchInfoResponse?.employee_info?.branchRole?.code ||
            branchInfoResponse?.data?.employee_info?.branchRole?.code;

          setUserRole(userRole);

          let businessName = '';
          let branchName = '';

          if (branchInfoResponse?.employee_info?.branch) {
            businessName = branchInfoResponse.employee_info.branch.business?.company_name || '';
            branchName = branchInfoResponse.employee_info.branch.branch_name || '';
          } else if (branchInfoResponse?.branch_info.business) {
            businessName = branchInfoResponse.branch_info.business?.company_name || '';
            branchName = branchInfoResponse.branch_info.branch_name || '';
          }

          setBusinessState({
            businessName,
            branchName
          });
        }

        const posDevices = Array.isArray(posResponse) ? posResponse : [posResponse];

        if (userRole) {
          if (userRole === 'KITCHEN') {
            setDeviceState(prev => ({
              ...prev,
              list: processKitchenDevices(kitchenResponse)
            }));
          } else if (userRole === 'CASHIER') {
            setDeviceState(prev => ({
              ...prev,
              list: processPosDevices(posDevices)
            }));
          } else {
            const combined = [...processPosDevices(posDevices), ...processKitchenDevices(kitchenResponse)];
            setDeviceState(prev => ({
              ...prev,
              list: combined
            }));
          }
        } else {
          const combined = [...processPosDevices(posDevices), ...processKitchenDevices(kitchenResponse)];
          setDeviceState(prev => ({
            ...prev,
            list: combined
          }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchAllData();
  }, [businessSlug, dispatch, processPosDevices, processKitchenDevices, userRole]);

  const calculateTotalCash = data => {
    if (!data) return 0;

    const cashDenominations = [
      { key: 'cash_1000', value: 1000 },
      { key: 'cash_500', value: 500 },
      { key: 'cash_100', value: 100 },
      { key: 'cash_50', value: 50 },
      { key: 'cash_20', value: 20 },
      { key: 'cash_10', value: 10 },
      { key: 'cash_5', value: 5 },
      { key: 'cash_1', value: 1 }
    ];

    return cashDenominations.reduce((total, { key, value }) => {
      return total + (data[key] || 0) * value;
    }, 0);
  };

  const getUserFullName = () => {
    const { profile } = userState;
    if (!profile) return 'กำลังโหลด...';
    return `${profile.first_name || ''} ${profile.last_name || ''}`;
  };

  const checkUserImportantTask = async device => {
    try {
      const params = device.deviceType === 'POS' ? { branch_pos_id: device.id } : { branch_kitchen_id: device.id };

      const endpoint =
        device.deviceType === 'POS' ? posHandler.checkImportantTask : posHandler.checkKitchenImportantTask;

      const response = await dispatch(endpoint(businessSlug, params));

      let isImportantTask;
      if (response.code !== 500 || response.code !== 400) {
        isImportantTask = response.message === 'USER_IN_IMPORTANT_TASK_POS';
      }

      setUserState(prev => ({
        ...prev,
        isInImportantTask: isImportantTask
      }));

      return isImportantTask;
    } catch (error) {
      console.error(`Error checking important task status:`, error);
      return false;
    }
  };

  const handleStartSelling = data => {
    setSessionState(prev => ({ ...prev, startData: data }));

    const totalCash = calculateTotalCash(data);
    if (totalCash > 0) {
      updateModalState('isStartupConfirmationOpen', true);
    } else {
      updateModalState('isNoCashFloatWarningOpen', true);
    }

    updateModalState('isStartSellingOpen', false);
  };

  const handleInitializeCashLog = async data => {
    try {
      const { selected } = deviceState;

      const params = {};
      let endpoint;

      if (selected?.deviceType === 'POS') {
        params.branch_pos_id = selected.id;
        endpoint = posHandler.branchPosLogin;
      } else {
        params.branch_kitchen_id = selected.id;
        endpoint = posHandler.branchKitchenLogin;
      }

      await dispatch(endpoint(businessSlug, params));
      const response = await dispatch(posHandler.initializePosCashLog(businessSlug, data));

      if (selected?.deviceType === 'Kitchen') {
        localStorage.setItem('branchKitchenId', selected.id);
        navigate(`/${businessSlug}/kitchen`);
      } else {
        localStorage.setItem('branchPOSId', selected.id);
        updateModalState('isStartPrintPopupOpen', true);
      }

      return response;
    } catch (error) {
      console.error('Error initializing cash log:', error);
      updateModalState('isPrinterErrorOpen', true);
    }
  };

  const handleDeviceSelection = async device => {
    setDeviceState(prev => ({ ...prev, selected: device }));

    if (device.isStarted) {
      const isImportantTask = await checkUserImportantTask(device);
      setUserState(prev => ({
        ...prev,
        isInImportantTask: isImportantTask
      }));

      updateModalState('isDeviceInUseWarningOpen', true);
      return;
    }

    updateModalState('isStartSellingOpen', true);
  };

  const proceedToStartSelling = async () => {
    try {
      const { selected } = deviceState;
      updateModalState('isDeviceInUseWarningOpen', false);

      if (selected) {
        const params =
          selected.deviceType === 'POS' ? { branch_pos_id: selected.id } : { branch_kitchen_id: selected.id };

        const endpoint = selected.deviceType === 'POS' ? posHandler.branchPosLogin : posHandler.branchKitchenLogin;

        await dispatch(endpoint(businessSlug, params));

        if (selected.deviceType === 'Kitchen') {
          localStorage.setItem('branchKitchenId', selected.id);
          navigate(`/${businessSlug}/kitchen`);
        } else {
          localStorage.setItem('branchPOSId', selected.id);
          if (selected.isStarted) {
            navigate(`/${businessSlug}/`);
          } else {
            updateModalState('isStartPrintPopupOpen', true);
          }
        }
      }
    } catch (error) {
      console.error('Error during login:', error);
    }
  };

  const handleLogout = async () => {
    setShowLogoutModal(true);
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    localStorage.removeItem('email');
    localStorage.removeItem('branchId');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('branchKitchenId');
    localStorage.removeItem('branchPOSId');
    socketService.disconnect();
    await dispatch(authHandler.logout(businessSlug));
    navigate(`/${businessSlug}/login`, { replace: true });
  };

  useEffect(() => {
    const { selected } = deviceState;

    if (skipSetting && selected?.id !== undefined) {
      if (selected.isStarted) {
        updateModalState('isDeviceInUseWarningOpen', true);
        checkUserImportantTask(selected).then(isImportantTask => {
          setUserState(prev => ({
            ...prev,
            isInImportantTask: isImportantTask
          }));
        });

        return;
      }
    }
  }, [deviceState, skipSetting, businessSlug, navigate]);

  const handlePrintComplete = () => {
    updateModalState('isStartPrintPopupOpen', false);
    setTimeout(() => {
      navigate(`/${businessSlug}/`);
    }, 1000);
  };

  useEffect(() => {
    const style = document.createElement('style');
    // style.innerHTML = `
    //   @media print {
    //     body * {
    //       visibility: hidden;
    //     }
    //     #section-to-print, #section-to-print * {
    //       visibility: visible !important;
    //       display: block !important;
    //     }
    //     #section-to-print {
    //       position: absolute;
    //       left: 0;
    //       top: 0;
    //       width: 100%;
    //       margin: 0;
    //       padding: 10mm;
    //     }
    //     .hidden {
    //       display: block !important;
    //     }
    //   }
    // `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="px-[min(1.95vw,20px)] py-[min(2.73vw,28px)]">
      <div className="flex justify-between">
        <div className="text-20 flex gap-[min(1.46vw,15px)] font-[500]">
          <div>
            ชื่อธุรกิจ: <span className="font-[400]">{businessState.businessName}</span>
          </div>
          <div>
            สาขา: <span className="font-[400]">{businessState.branchName.replace('สาขา ', '')}</span>
          </div>
        </div>
        <div className="flex items-center gap-[min(0.98vw,10px)]">
          <button
            onClick={() => setShowPrinterSettings(true)}
            className="flex items-center gap-[min(0.49vw,5px)] px-[min(1.17vw,12px)] py-[min(0.59vw,6px)] rounded-[min(0.78vw,8px)] border-[min(0.10vw,1.5px)] border-[#DCDCDC] bg-white text-14 font-[500] text-[#333] hover:bg-[#F9F9F9]"
          >
            <img src="/img/print.svg" alt="" className="w-[min(1.46vw,15px)] h-[min(1.46vw,15px)]" />
            ตั้งค่าปริ้นเตอร์
          </button>
          <PosAvartarCard
            userInfo={{
              fullName: getUserFullName(),
              code: userState.profile?.code || '-',
              avartar: '/img/avatar-female.png'
            }}
            handleLogout={handleLogout}
          />
        </div>
      </div>

      <div className="w-full h-[min(0.10vw,1px)] bg-[#EDEDED] mb-[min(1.86vw,19px) mt-[min(1.86vw,12px)]"></div>

      <div className="flex flex-wrap gap-[min(1.27vw,13px)]">
        {deviceState.list.map((device, index) => (
          <PosSettingCard
            key={index}
            posInfo={{
              posName: device.posInfo.posName
            }}
            userInfo={device.userInfo}
            modalPosSettingStart={{
              modalPosSettingStartOpen: modalStates.isStartSellingOpen,
              setModalPosSettingStartOpen: () => handleDeviceSelection(device)
            }}
            modalPosDubStartConfirm={{
              modalOpen: modalStates.isDeviceInUseWarningOpen,
              setModalOpen: () => handleDeviceSelection(device)
            }}
            isStarted={device.isStarted}
            deviceType={device.deviceType}
            device={device}
            businessSlug={businessSlug}
          />
        ))}
      </div>

      <POSStartSellingModal
        modalPosSettingStart={{
          modalPosSettingStartOpen: modalStates.isStartSellingOpen,
          setModalPosSettingStartOpen: () => updateModalState('isStartSellingOpen', false)
        }}
        startValueObj={{
          startValue: sessionState.initialCashAmount,
          setStartValue: value => setSessionState(prev => ({ ...prev, initialCashAmount: value }))
        }}
        selectedDevice={deviceState.selected}
        onStartSelling={handleStartSelling}
        skipMoneySetting={skipSetting}
      />

      <POSStartupConfirmationModal
        modalOpenObj={{
          modalOpen: modalStates.isStartupConfirmationOpen,
          setModalOpen: () => updateModalState('isStartupConfirmationOpen', false)
        }}
        selectedDevice={deviceState.selected}
        sessionStartData={sessionState.startData}
        userProfile={userState.profile}
        businessName={businessState.businessName}
        branchName={businessState.branchName}
        onConfirm={data => {
          handleInitializeCashLog(data);
          updateModalState('isStartupConfirmationOpen', false);
        }}
      />

      <NoCashFloatWarningModal
        modalOpenObj={{
          modalOpen: modalStates.isNoCashFloatWarningOpen,
          setModalOpen: () => updateModalState('isNoCashFloatWarningOpen', false)
        }}
        sessionStartData={sessionState.startData}
        onConfirm={data => {
          handleInitializeCashLog(data);
          updateModalState('isNoCashFloatWarningOpen', false);
        }}
      />

      <POSInUseWarningModal
        modalOpenObj={{
          modalOpen: modalStates.isDeviceInUseWarningOpen,
          setModalOpen: () => updateModalState('isDeviceInUseWarningOpen', false)
        }}
        selectedDevice={deviceState.selected}
        sessionStartData={sessionState.startData}
        isUserInImportantTask={userState.isInImportantTask}
        onConfirm={proceedToStartSelling}
        onCancel={() => updateModalState('isDeviceInUseWarningOpen', false)}
      />

      <PrinterOutOfPaperModal
        modalOpenObj={{
          modalOpen: modalStates.isPrinterErrorOpen,
          setModalOpen: () => updateModalState('isPrinterErrorOpen', false)
        }}
      />

      <LogoutConfirmModal isOpen={showLogoutModal} onClose={handleCancelLogout} onConfirm={handleConfirmLogout} />

      <ModalPrinterSettings isOpen={showPrinterSettings} onClose={() => setShowPrinterSettings(false)} />

      <ModalPOSCashTransactionPrint
        isOpen={modalStates.isStartPrintPopupOpen}
        printType="start"
        cashBreakdown={sessionState.startData}
        businessName={businessState.businessName}
        branchName={businessState.branchName}
        taxNumber={deviceState.selected?.posInfo?.vatCode}
        posNo={deviceState.selected?.posInfo?.posNo}
        posName={deviceState.selected?.posInfo?.posName}
        userFullName={getUserFullName()}
        onPrintComplete={handlePrintComplete}
      />
    </div>
  );
}
